import { OrderPrepareDocExt, RETURN_ORDER_STATUS } from 'khufu-legos';
import { Badge, Card, Col, Row, Table } from 'react-bootstrap';

import { currencyFormatter } from '../../../helpers/currencyFormatter';
import getStatusColor from '../../../helpers/getStatusColor';
import { Customer, Order, ReturnOrder } from '../../../types';
import { capitalize } from '../../../utils/capitalize';
import { formatDateShort } from '../../../utils/formatShortDate';
import { formatTime } from '../../../utils/formatTime';
import OrderReturns from './OrderReturns';

// One line of the "as ordered" table. The snapshot's rows are slim, the live
// rows are full OrderItemsDoc; both render through this.
type OrderedRow = {
  key: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  originalPrice?: number;
  discount?: number;
};

interface OrderSummaryProps {
  customer: Customer | Omit<Customer, 'createdBy' | 'updatedBy'>;
  order?: Order;
  orderPrepare?: OrderPrepareDocExt;
  returnOrder?: ReturnOrder;
  showNotes?: boolean;
  title?: string;
}

const OrderSummary = ({
  customer,
  order,
  orderPrepare,
  returnOrder,
  showNotes = false,
  title = 'Order Summary'
}: OrderSummaryProps) => {
  const currentOrder = order || orderPrepare || returnOrder;
  const orderDrivers = order?.drivers;
  const orderMotorboys = order?.motorboys;

  if (!currentOrder) {
    return null;
  }

  // Ordered - returned = due. The items "as ordered" come from `original`, which
  // the backend rebuilds from the kept and returned rows. Prepare-order and
  // return-order views have no such snapshot; their live items are the whole story.
  const orderedItems: OrderedRow[] = order?.original
    ? order.original.items.map((item, index) => ({ key: String(index), ...item }))
    : currentOrder.items.map(item => ({ key: item._id.toString(), ...item }));
  const orderedTotal = order?.original?.total ?? orderedItems.reduce((acc, item) => acc + item.total, 0);
  const orderReturns = order?.returns ?? [];
  const hasReturns = orderReturns.length > 0;
  const returnedTotal =
    order?.returnsSummary?.total ??
    orderReturns
      .filter(orderReturn => orderReturn.status !== RETURN_ORDER_STATUS.CANCELED)
      .reduce((acc, orderReturn) => acc + orderReturn.total, 0);

  return (
    <Card>
      <Card.Body className="p-5">
        <Row className="mb-6 px-4">
          <Col className="text-end">
            <Badge bg={`${getStatusColor(order?.status ?? '')}-soft`}>{order?.status}</Badge>
          </Col>
        </Row>
        <Row>
          <Col className="text-center">
            <h2 className="mb-6">{title}</h2>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}></Col>
          <Col xs={12} md={6} className="text-md-end customer-info">
            <h6 className="text-uppercase text-muted px-4">Customer</h6>
            <p className="text-muted mb-4 px-4">
              <strong className="text-body">{customer.name}</strong> <br />
              {customer.phoneNumber} <br />
              {customer.customerType} <br />
            </p>
            <h6 className="text-uppercase text-muted px-4">Order Type</h6>
            <p className="text-muted mb-4 px-4 capitalize">
              {order?.type ? capitalize(order.type) : returnOrder?.type ? capitalize(returnOrder.type) : ''} <br />
            </p>
            <h6 className="text-uppercase text-muted px-4">Date</h6>
            <p className="mb-4 px-4">
              <time dateTime="2018-04-23">{formatDateShort(currentOrder.createdAt)}</time>
            </p>
            <h6 className="text-uppercase text-muted px-4">Time</h6>
            <p className="mb-4 px-4">
              <time dateTime="2018-04-23">{formatTime(currentOrder.createdAt)}</time>
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Row>
              <Col className="text-center order-items-section">
                <h4 className="mt-5">Order Items</h4>
                {hasReturns && <p className="text-muted">As ordered</p>}
              </Col>
            </Row>
            <Table className="my-4" responsive>
              <thead>
                <tr>
                  <th className="px-4 bg-transparent border-top-0">
                    <span className="h6">Product</span>
                  </th>
                  <th className="px-4 bg-transparent border-top-0 text-end">
                    <span className="h6">Qty</span>
                  </th>
                  <th className="px-4 bg-transparent border-top-0 text-end">
                    <span className="h6">Price</span>
                  </th>
                  <th className="px-4 bg-transparent border-top-0 text-end">
                    <span className="h6">Total</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderedItems.map(item => (
                  <tr key={item.key}>
                    <td className="px-4">{item.name}</td>
                    <td className="px-4 text-end">{item.quantity}</td>
                    <td className="px-4 text-end">
                      {item.discount && item.discount > 0 && item.originalPrice !== undefined ? (
                        <>
                          <span className="text-muted text-decoration-line-through me-2">
                            {currencyFormatter(item.originalPrice)}
                          </span>
                          {currencyFormatter(item.price)}
                        </>
                      ) : (
                        currencyFormatter(item.price)
                      )}
                    </td>
                    <td className="px-4 text-end">{currencyFormatter(item.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="px-4 border-top border-top-2">
                    <strong>{hasReturns ? 'Ordered total' : 'Total'}</strong>
                  </td>
                  <td colSpan={3} className="px-4 text-end border-top border-top-2">
                    <span className="h3">{currencyFormatter(orderedTotal)}</span>
                  </td>
                </tr>
              </tbody>
            </Table>
            <OrderReturns returns={orderReturns} returnedTotal={returnedTotal} />
            <Table className="my-4" responsive>
              <tbody>
                <tr>
                  <td className="px-4 border-top border-top-2">
                    <strong>Amount due</strong>
                  </td>
                  <td className="px-4 text-end border-top border-top-2">
                    <span className="h3">{currencyFormatter(currentOrder.total)}</span>
                  </td>
                </tr>
              </tbody>
            </Table>
            {showNotes && order && (
              <>
                <hr className="my-5" />
                {!!orderDrivers?.length && (
                  <>
                    <h6 className="text-uppercase">Drivers</h6>
                    {order?.drivers?.map(driver => (
                      <p key={driver?._id?.toString()} className="text-muted mb-0">
                        {driver?.name}
                      </p>
                    ))}
                  </>
                )}

                {!!orderMotorboys?.length && (
                  <div className="mt-5">
                    <h6 className="text-uppercase">Motorboys</h6>
                    {order?.motorboys?.map(motorboy => (
                      <p key={motorboy?._id?.toString()} className="text-muted mb-0">
                        {motorboy?.name}
                      </p>
                    ))}
                  </div>
                )}
              </>
            )}
            {order?.comments && (
              <>
                <hr className="my-5" />
                <h6 className="text-uppercase">Comments</h6>
                <p className="text-muted mb-0">{order.comments}</p>
              </>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default OrderSummary;
